import React, { FC, useEffect, useState } from 'react';
import { BatchesFilterSection, IBatchesFiltersParams } from '../../components/batches/BatchesFilterSection';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { fetchWhiteListedPartnersList } from '../../store/merchnts/actions/partners';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { Navbar } from '../../components/Navbar';
import { useLocation } from 'react-router';
import {
  BATCHES_BASE_URL,
  BATCHES_TABS,
  getInitialBatchesNavTab,
} from '../../routing/batchesRoutes';
import { useNavigate } from 'react-router-dom';
import { makeSearchParams, parseSearchParams } from '../../utils/utilities';
import { fetchBatchData, fetchShelvedBatchesData, fetchUnpostedBatchData } from '../../store/payments/actions/batches';
import { usePaymentContext } from '../../store/payments/payments-context';
import { PageTitle } from '../../layout/core';
import { batchesBreadCrumbs } from './BatchesListPage';
import { useTranslation } from 'react-i18next';
import { paymentsActionTypes } from '../../store/payments/payments-action-types';

const batchesTabs = [
  {
    label: 'Unposted batches',
    value: BATCHES_TABS.UNPOSTED_BATCHES,
  },
  {
    label: 'Posted batches',
    value: BATCHES_TABS.BATCHES,
  },
  {
    label: 'Shelved batches',
    value: BATCHES_TABS.SHELVED_BATCHES,
  },
];

interface Props {
  children: React.ReactElement;
}

export const BatchesPage: FC<Props> = ({ children }) => {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);

  const { merchantState: { chosenMerchant }, dispatch: merchantDispatch } = useMerchants();
  const { dispatch } = usePaymentContext();

  const [activeNavTab, setActiveNavTab] = useState<BATCHES_TABS>(getInitialBatchesNavTab(pathname));

  const [batchType, setBatchType] = useState<string[]>([]);
  const [partnerId, setPartnerId] = useState<string>('');
  const [yearAndMonth, setYearAndMonth] = useState<string>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [appliedFiltersState, setAppliedFiltersState] = useState<IBatchesFiltersParams>(null);
  const [isBatchesLoading, setIsBatchesLoading] = useState<boolean>(true);

  const filtersFromUrl: IBatchesFiltersParams = parseSearchParams(search) || {};

  useEffect(() => {
    if (checkRoles(authUser, CLAIMS.SUPER_ADMIN)) {
      void fetchWhiteListedPartnersList(merchantDispatch);
    }
  }, []);

  useEffect(() => {
    if (!search) {
      const initialFilters: IBatchesFiltersParams = {
        partnerId,
        yearAndMonth,
        query: searchValue,
        batchType,
      };

      setIsBatchesLoading(false);
      setAppliedFiltersState(initialFilters);
    }
  }, []);

  useEffect(() => {
    return () => {
      dispatch({ type: paymentsActionTypes.FETCH_UNPOSTED_BATCHES_DATA, payload: [] });
      dispatch({ type: paymentsActionTypes.FETCH_BATCHES_DATA, payload: [] });
      dispatch({ type: paymentsActionTypes.FETCH_SHELVED_BATCHES_DATA, payload: [] });
    };
  }, []);

  const handleTabChange = (tab: BATCHES_TABS): void => {
    setActiveNavTab(tab);

    navigate(`${BATCHES_BASE_URL}/${tab}?${makeSearchParams(filtersFromUrl)}`);
  };

  const listener = async (searchParams: IBatchesFiltersParams): Promise<void> => {
    setIsBatchesLoading(true);

    let localParams: IBatchesFiltersParams;

    if (Object.keys(searchParams).length) {

      localParams = { ...searchParams };

      setPartnerId(searchParams?.partnerId ?? '');
      setYearAndMonth(searchParams?.yearAndMonth ?? null);
      setSearchValue(searchParams?.query ?? '');

      if (searchParams?.batchType && typeof searchParams?.batchType === 'string') {
        setBatchType([searchParams?.batchType]);
      } else {
        setBatchType(searchParams?.batchType ?? []);
      }

      const initialFilters: IBatchesFiltersParams = {
        partnerId: searchParams?.partnerId ?? '',
        yearAndMonth: searchParams?.yearAndMonth ?? null,
        query: searchParams?.query ?? '',
        batchType: searchParams?.batchType && typeof searchParams?.batchType === 'string'
          ? [searchParams.batchType]
          : searchParams?.batchType ?? [],
      };

      setAppliedFiltersState(initialFilters);



    } else {
      localParams = {
        merchantid: chosenMerchant,
      };
    }

    switch (activeNavTab) {
      case BATCHES_TABS.UNPOSTED_BATCHES:
        await fetchUnpostedBatchData({ dispatch, params: localParams });
        break;
      case BATCHES_TABS.BATCHES:
        await fetchBatchData({ dispatch, params: localParams });
        break;
      case BATCHES_TABS.SHELVED_BATCHES:
        await fetchShelvedBatchesData({ dispatch, params: localParams });
        break;
    }

    dispatch({ type: paymentsActionTypes.SET_UNPOSTED_BATCHES_DATA_LOADING, payload: false });
    dispatch({ type: paymentsActionTypes.SET_BATCHES_DATA_LOADING, payload: false });
    dispatch({ type: paymentsActionTypes.SET_SHELVED_BATCHES_DATA_LOADING, payload: false });

    setIsBatchesLoading(false);
  };

  return (
    <>
      <PageTitle breadcrumbs={batchesBreadCrumbs}>{t('MENU.PAYOUTS')}</PageTitle>

      <BatchesFilterSection
        batchType={batchType}
        setBatchType={setBatchType}
        partnerId={partnerId}
        setPartnerId={setPartnerId}
        yearAndMonth={yearAndMonth}
        setYearAndMonth={setYearAndMonth}
        query={searchValue}
        setQuery={setSearchValue}
        appliedFiltersState={appliedFiltersState}
        setAppliedFiltersState={setAppliedFiltersState}
        isBatchesLoading={isBatchesLoading}
      />

      <Navbar
        tabsList={batchesTabs}
        active={activeNavTab}
        onSelect={handleTabChange}
      />

      {React.cloneElement(children, { listener })}

    </>
  );
};